// Folha de pagamento: cálculo mensal por funcionário.
//
//   apurado       = fixo + comissão + bônus
//   descontado    = parte do vale (tomado no mês + saldo de meses anteriores) que cabe no que sobra do apurado
//   a pagar       = apurado − descontado − já recebido
//   restante      = vale que não coube (nem o que a pessoa recebeu a mais): desconta na folha do mês seguinte
//
// - Comissão = % da base. A base é o valor LÍQUIDO (depois de taxas e impostos da forma de pagamento)
//   RECEBIDO no mês: dinheiro que entrou em setembro conta em setembro, mesmo de venda de agosto.
//     own_sales  → só o que entrou das vendas do próprio funcionário, com um % fixo
//     acm_levels → igual, mas o % é o do nível do ranking ACM no mês (2% / 2,3% / 2,5% / 3% / 4%)
//     team_sales → o que entrou das vendas da equipe comercial (gestor comercial: 5%).
//                  Equipe = funcionários com comissão sobre vendas próprias (own_sales / acm_levels)
// - Vale/adiantamento já saiu do caixa, então é descontado do que a pessoa ganhou. "Já recebido" é o que ela
//   já recebeu por fora antes do fechamento (ex.: R$ 2.000 já transferidos). O desconto cabe no que sobra do
//   apurado depois do já recebido; o resto do vale fica de saldo para a folha seguinte.
// - Rateio entre empresas: parte do CUSTO da pessoa (fixo + comissão + bônus) pode ser do Orion CRM; o restante
//   é custo da empresa que roda a folha (Evolua). O rateio só muda onde o custo aparece (DRE), não o que a
//   pessoa recebe nem o que sai do caixa.
// - O que ainda falta descontar (restante do vale, ou valor recebido a mais) vira saldo do mês seguinte: nada é
//   pago ao funcionário enquanto ele deve.
//
// Exemplo (Tássio, setembro): apurado 2.709; vale 1.500; já recebido 2.000.
//   sobra para descontar: 2.709 − 2.000 = 709 → descontado do vale: 709; a pagar: 2.709 − 709 − 2.000 = 0
//   restante do vale: 1.500 − 709 = 791 → desconta na folha de outubro.
//
// Arquivo sem import de Deno: testado com vitest, usado pela edge function payroll-automation e pela tela.

import {
    ConfigFormas,
    EventoRecebimento,
    eventosDoMes,
    liquidoDosEventos,
    somarEventos,
} from './recebiveis.ts';
import { NIVEIS_ACM } from './niveisAcm.ts';

export type BaseComissao = 'none' | 'own_sales' | 'acm_levels' | 'team_sales';

export const ROTULO_BASE_COMISSAO: Record<BaseComissao, string> = {
    none: 'Sem comissão',
    own_sales: 'Vendas do próprio funcionário (% fixo)',
    acm_levels: 'Vendas do próprio funcionário (% por nível ACM)',
    team_sales: 'Vendas da equipe comercial',
};

/** Categoria das despesas lançadas em Contas a Pagar pela folha. */
export const CATEGORIA_FOLHA = 'Folha de Pagamento';

/** Papéis que entram no "time comercial" ALÉM dos funcionários com comissão sobre vendas próprias (opcional). */
export const PAPEIS_EQUIPE_PADRAO: string[] = [];

/** Comissão calculada sobre as vendas do próprio funcionário (precisa do usuário vinculado). */
export const baseDeVendasProprias = (b: BaseComissao) => b === 'own_sales' || b === 'acm_levels';

export const DIA_PAGAMENTO_PADRAO = 10;

export interface FuncionarioFolha {
    id: string;
    nome: string;
    /** Usuário (login) vinculado: é por ele que se acham as vendas do funcionário. */
    userId?: string | null;
    fixo: number;
    comissaoPercent: number;
    comissaoBase: BaseComissao;
}

export interface AjusteManual {
    bonus: number;
    pago: number;
    /** Base de comissão digitada à mão (substitui a calculada) */
    baseManual: number | null;
    notas: string | null;
    /** Parte do custo da pessoa que é do Orion CRM */
    orion: number;
    /** Vale a descontar nesta folha definido à mão (null/ausente = automático) */
    descontoVale?: number | null;
}

export interface DetalheComissao {
    /** Recebimentos que entraram na base */
    recebimentos: number;
    brutoRecebido: number;
    liquidoRecebido: number;
    baseManual: boolean;
    /** Comissão sobre vendas próprias, mas o funcionário não tem usuário vinculado */
    semUsuario: boolean;
    /** Nível ACM do mês (base acm_levels) */
    nivel?: string;
}

export interface EntradaFolha {
    employeeId: string;
    nome: string;
    fixo: number;
    comissaoBase: number;
    comissaoPercent: number;
    comissaoValor: number;
    bonus: number;
    /** Vale/adiantamento TOMADO no mês (lançado em Vales) */
    valeTomado: number;
    /** Vale DESCONTADO nesta folha (do tomado no mês e do saldo que veio de meses anteriores) */
    vales: number;
    /** Valor do vale a descontar definido à mão (null/ausente = automático) */
    descontoManual?: number | null;
    /** Já recebido: o que a pessoa já recebeu por fora antes do fechamento (líquido, sem contar o vale) */
    pago: number;
    /** Dívida herdada do mês anterior (0 ou negativo) */
    saldoAnterior: number;
    /** Restante do vale (e do que foi pago a mais) que passa para a folha seguinte (0 ou mais) */
    saldoVale: number;
    /** A pagar (líquido). Só fica negativo se a pessoa recebeu mais do que o apurado. */
    total: number;
    /** Parte do custo (fixo + comissão + bônus) que é do Orion CRM; o restante é custo da Evolua */
    orion: number;
    notas: string | null;
    detalhes: DetalheComissao;
}

const num = (n: unknown) => Number(n) || 0;
export const arred = (n: number) => Math.round(n * 100) / 100;

export const comissaoDe = (base: number, percent: number): number => arred(num(base) * num(percent) / 100);

/** O que a pessoa ganhou no mês (fixo + comissão + bônus). */
export const apuradoDaEntrada = (e: Pick<EntradaFolha, 'fixo' | 'comissaoValor' | 'bonus'>): number =>
    arred(num(e.fixo) + num(e.comissaoValor) + num(e.bonus));

/**
 * Desconto do vale. O que sobra do apurado depois do que a pessoa já recebeu é o máximo que dá para descontar;
 * o resto do vale (e qualquer valor recebido a mais) vira saldo para a folha seguinte.
 *   Ex. Tássio: apurado 2.709, vale 1.500, já recebido 2.000 → desconta 709, líquido 0, restante 791.
 */
export function calcularDescontos(p: { apurado: number; valeTomado: number; saldoAnterior: number; pago: number; descontoManual?: number | null }) {
    const apurado = arred(num(p.apurado));
    const pago = arred(num(p.pago));
    const dividas = arred(num(p.valeTomado) + Math.abs(Math.min(num(p.saldoAnterior), 0)));
    // Automático: só o que cabe no que sobra do apurado. Manual: o valor combinado, sem passar da dívida nem do apurado.
    const descontado = p.descontoManual != null
        ? arred(Math.min(dividas, Math.max(num(p.descontoManual), 0), Math.max(apurado - pago, 0)))
        : arred(Math.min(dividas, Math.max(apurado - pago, 0)));
    const excesso = arred(Math.max(pago - apurado, 0));
    return {
        descontado,
        saldoVale: arred(dividas - descontado + excesso),
        total: arred(apurado - descontado - pago),
    };
}

/** Custo da pessoa no mês: o que ela ganhou (fixo + comissão + bônus). Vale e valor já pago são só acerto de caixa. */
export const custoDaEntrada = (e: Pick<EntradaFolha, 'fixo' | 'comissaoValor' | 'bonus'>): number =>
    arred(num(e.fixo) + num(e.comissaoValor) + num(e.bonus));

/** Parte do custo que fica com a Evolua (o que não foi para o Orion). */
export const custoEvolua = (e: Pick<EntradaFolha, 'fixo' | 'comissaoValor' | 'bonus' | 'orion'>): number =>
    arred(Math.max(custoDaEntrada(e) - num(e.orion), 0));

/** Refaz comissão e total depois que alguém muda base, percentual, bônus ou valor já pago. */
export function recalcularEntrada(e: EntradaFolha): EntradaFolha {
    const comissaoValor = comissaoDe(e.comissaoBase, e.comissaoPercent);
    const custo = custoDaEntrada({ fixo: e.fixo, comissaoValor, bonus: e.bonus });
    // O rateio nunca passa do custo da pessoa
    const orion = Math.min(Math.max(arred(num(e.orion)), 0), custo);
    const d = calcularDescontos({ apurado: custo, valeTomado: e.valeTomado, saldoAnterior: e.saldoAnterior, pago: e.pago, descontoManual: e.descontoManual });
    return { ...e, comissaoValor, orion, vales: d.descontado, saldoVale: d.saldoVale, total: d.total };
}

/** O que realmente precisa sair do caixa (nunca negativo). */
export const valorAPagar = (e: Pick<EntradaFolha, 'total'>): number => Math.max(arred(e.total), 0);

/** Quanto o funcionário ainda deve (restante do vale) e passa para o mês seguinte (0 ou negativo). */
export const saldoParaProximoMes = (e: Pick<EntradaFolha, 'saldoVale'>): number => -arred(Math.max(num(e.saldoVale), 0));

export interface ParametrosFolha {
    /** AAAA-MM */
    mes: string;
    funcionarios: FuncionarioFolha[];
    /** Todos os recebimentos conhecidos (qualquer mês); o motor filtra o mês. */
    eventos: EventoRecebimento[];
    config?: ConfigFormas | null;
    /** Usuários que formam o time comercial (base team_sales). */
    equipeUserIds: Iterable<string>;
    /** userId → índice do nível ACM no mês (base acm_levels) */
    niveisAcm?: Record<string, number>;
    /** employeeId → vales/adiantamentos TOMADOS no mês */
    vales?: Record<string, number>;
    /** employeeId → dívida (negativa: restante de vale) herdada do mês anterior */
    saldoAnterior?: Record<string, number>;
    /** employeeId → edições manuais a preservar ao recalcular o rascunho */
    manuais?: Record<string, Partial<AjusteManual>>;
}

export function montarFolha(p: ParametrosFolha): EntradaFolha[] {
    const doMes = eventosDoMes(p.eventos, p.mes);
    const equipe = new Set(p.equipeUserIds);

    return p.funcionarios.map(f => {
        const manual = p.manuais?.[f.id] || {};
        const nivel = f.comissaoBase === 'acm_levels' && f.userId ? NIVEIS_ACM[p.niveisAcm?.[f.userId] ?? 0] : undefined;
        const baseCalculada: EventoRecebimento[] =
            baseDeVendasProprias(f.comissaoBase)
                ? (f.userId ? doMes.filter(e => e.userId === f.userId) : [])
                : f.comissaoBase === 'team_sales'
                    ? doMes.filter(e => !!e.userId && equipe.has(e.userId))
                    : [];

        const liquido = liquidoDosEventos(baseCalculada, p.config);
        const baseManual = manual.baseManual != null ? num(manual.baseManual) : null;
        const comissaoBase = baseManual ?? liquido;

        const entrada: EntradaFolha = {
            employeeId: f.id,
            nome: f.nome,
            fixo: arred(num(f.fixo)),
            comissaoBase: arred(comissaoBase),
            comissaoPercent: nivel ? nivel.commission : num(f.comissaoPercent),
            comissaoValor: 0,
            bonus: arred(num(manual.bonus)),
            valeTomado: arred(num(p.vales?.[f.id])),
            vales: 0,
            descontoManual: manual.descontoVale ?? null,
            pago: arred(num(manual.pago)),
            saldoAnterior: arred(Math.min(num(p.saldoAnterior?.[f.id]), 0)),
            saldoVale: 0,
            total: 0,
            orion: arred(num(manual.orion)),
            notas: manual.notas ?? null,
            detalhes: {
                recebimentos: baseCalculada.length,
                brutoRecebido: somarEventos(baseCalculada),
                liquidoRecebido: liquido,
                baseManual: baseManual != null,
                semUsuario: baseDeVendasProprias(f.comissaoBase) && !f.userId,
                ...(nivel ? { nivel: nivel.name } : {}),
            },
        };
        return recalcularEntrada(entrada);
    });
}

export function totaisDaFolha(entradas: EntradaFolha[]) {
    return {
        fixo: arred(entradas.reduce((s, e) => s + e.fixo, 0)),
        comissoes: arred(entradas.reduce((s, e) => s + e.comissaoValor, 0)),
        bonus: arred(entradas.reduce((s, e) => s + e.bonus, 0)),
        valesTomados: arred(entradas.reduce((s, e) => s + e.valeTomado, 0)),
        vales: arred(entradas.reduce((s, e) => s + e.vales, 0)),
        aPagar: arred(entradas.reduce((s, e) => s + valorAPagar(e), 0)),
        devendo: arred(entradas.reduce((s, e) => s + saldoParaProximoMes(e), 0)),
        custoOrion: arred(entradas.reduce((s, e) => s + num(e.orion), 0)),
        custoEvolua: arred(entradas.reduce((s, e) => s + custoEvolua(e), 0)),
    };
}

// ---------- Datas ----------

/** AAAA-MM → AAAA-MM do mês seguinte (ou anterior, com delta negativo). */
export function deslocarMes(mes: string, delta: number): string {
    const [a, m] = mes.split('-').map(Number);
    const idx = a * 12 + (m - 1) + delta;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

export function ultimoDiaDoMes(mes: string): string {
    const [a, m] = mes.split('-').map(Number);
    const dia = new Date(Date.UTC(a, m, 0)).getUTCDate();
    return `${mes}-${String(dia).padStart(2, '0')}`;
}

/** Data de pagamento da folha de `mes`: o dia combinado do mês seguinte (limitado ao último dia do mês). */
export function dataDePagamento(mes: string, dia: number = DIA_PAGAMENTO_PADRAO): string {
    const proximo = deslocarMes(mes, 1);
    const ultimo = Number(ultimoDiaDoMes(proximo).slice(8, 10));
    const d = Math.min(Math.max(Math.trunc(dia) || DIA_PAGAMENTO_PADRAO, 1), ultimo);
    return `${proximo}-${String(d).padStart(2, '0')}`;
}

export function descricaoDaDespesa(mes: string, nome: string): string {
    const [a, m] = mes.split('-');
    return `Folha ${m}/${a} — ${nome}`;
}

export function rotuloMes(mes: string): string {
    const nomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    const [a, m] = mes.split('-').map(Number);
    return `${nomes[m - 1]} de ${a}`;
}

// ---------- Documentos ----------

export const soDigitos = (v: string) => (v || '').replace(/\D/g, '');

export function formatarCpf(v: string): string {
    const d = soDigitos(v).slice(0, 11);
    return d
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

/** Confere os dígitos verificadores do CPF (rejeita sequências repetidas como 111.111.111-11). */
export function cpfValido(v: string): boolean {
    const d = soDigitos(v);
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
    const digito = (base: string, pesoInicial: number) => {
        const soma = base.split('').reduce((s, c, i) => s + Number(c) * (pesoInicial - i), 0);
        const r = (soma * 10) % 11;
        return r === 10 ? 0 : r;
    };
    return digito(d.slice(0, 9), 10) === Number(d[9]) && digito(d.slice(0, 10), 11) === Number(d[10]);
}

export type TipoChavePix = 'cpf' | 'cnpj' | 'email' | 'phone' | 'random';

export const ROTULO_TIPO_CHAVE_PIX: Record<TipoChavePix, string> = {
    cpf: 'CPF',
    cnpj: 'CNPJ',
    email: 'E-mail',
    phone: 'Celular',
    random: 'Chave aleatória',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const emailValido = (v: string) => EMAIL_RE.test((v || '').trim());

/** Valida a chave PIX conforme o tipo. Devolve a mensagem de erro, ou null quando está ok (ou vazia). */
export function validarChavePix(tipo: TipoChavePix | null | undefined, chave: string): string | null {
    const c = (chave || '').trim();
    if (!c) return null;
    switch (tipo) {
        case 'cpf': return cpfValido(c) ? null : 'CPF da chave PIX inválido.';
        case 'cnpj': return soDigitos(c).length === 14 ? null : 'CNPJ da chave PIX deve ter 14 dígitos.';
        case 'email': return emailValido(c) ? null : 'E-mail da chave PIX inválido.';
        case 'phone': {
            const d = soDigitos(c.replace(/^\+55/, ''));
            return d.length === 10 || d.length === 11 ? null : 'Celular da chave PIX deve ter DDD + número.';
        }
        case 'random': return UUID_RE.test(c) ? null : 'Chave aleatória deve estar no formato xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx.';
        default: return 'Escolha o tipo da chave PIX.';
    }
}
